import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Send, Paperclip, Download, User, Clock, Tag, FileText, X } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import { SOCKET_URL, SOCKET_PATH, UPLOAD_URL } from '../../config';

const statusColors = {
  'Yeni': 'bg-blue-100 text-blue-700 border-blue-200',
  'İşleme Alındı': 'bg-purple-100 text-purple-700 border-purple-200',
  'Devam Ediyor': 'bg-amber-100 text-amber-700 border-amber-200',
  'Beklemede': 'bg-gray-100 text-gray-600 border-gray-200',
  'Müşteri Yanıtı Bekleniyor': 'bg-pink-100 text-pink-700 border-pink-200',
  'Çözüldü': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Kapatıldı': 'bg-red-100 text-red-700 border-red-200',
};

export default function CustomerTicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadTicket();
    const socket = io(SOCKET_URL, { path: SOCKET_PATH });
    socket.emit('joinRoom', { userId: user?.id, ticketId: id });
    socket.on('newMessage', () => loadTicket());
    socket.on('ticketStatusChanged', (data) => { if (String(data.ticketId) === id) loadTicket(); });
    return () => socket.disconnect();
  }, [id, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const loadTicket = async () => {
    try {
      const res = await api.get(`/tickets/${id}`);
      setTicket(res.data);
    } catch (err) {
      toast.error('Ticket yüklenemedi');
    }
    setLoading(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() && files.length === 0) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', message);
      files.forEach(f => formData.append('files', f));
      await api.post(`/tickets/${id}/messages`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('');
      setFiles([]);
      loadTicket();
    } catch (err) {
      toast.error('Mesaj gönderilemedi');
    }
    setSending(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  if (!ticket) return <div className="text-center py-20 text-slate-500">Ticket bulunamadı</div>;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-mono text-slate-400">#{ticket.Id}</span>
            <h1 className="text-xl font-bold text-slate-800">{ticket.Title}</h1>
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className={`text-xs px-3 py-1 rounded-full font-medium border ${statusColors[ticket.Status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{ticket.Status}</span>
            <span className="text-xs text-slate-500 flex items-center gap-1"><Tag size={12} />{ticket.Category}</span>
            <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} />{new Date(ticket.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages */}
        <div className="lg:col-span-2 space-y-4">
          {/* Description */}
          {ticket.Description && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket.Description}</p>
            </div>
          )}

          {/* Messages List */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">Mesajlar</h3>
            </div>
            <div className="p-5 space-y-4 max-h-[500px] overflow-y-auto">
              {ticket.messages?.length === 0 ? (
                <p className="text-center text-slate-400 py-6 text-sm">Henüz mesaj yok</p>
              ) : ticket.messages?.map(msg => (
                <div key={msg.Id} className={`flex ${msg.UserId === user?.id ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.UserId === user?.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold ${msg.UserId === user?.id ? 'text-blue-100' : 'text-slate-500'}`}>
                        {msg.UserRole === 'customer' ? msg.UserName : `${msg.UserName} (Destek)`}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.Message}</p>
                    <p className={`text-xs mt-1 ${msg.UserId === user?.id ? 'text-blue-200' : 'text-slate-400'}`}>
                      {new Date(msg.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Send Message */}
            {!['Kapatıldı'].includes(ticket.Status) && (
              <form onSubmit={handleSend} className="border-t border-slate-100 p-4">
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {files.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-lg">
                        <FileText size={12} />{f.name}
                        <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 placeholder-slate-400 resize-none text-sm"
                      placeholder="Mesajınızı yazın..." />
                  </div>
                  <label className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl cursor-pointer transition-colors">
                    <Paperclip size={20} />
                    <input type="file" multiple onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5))} className="hidden" />
                  </label>
                  <button type="submit" disabled={sending}
                    className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {sending ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <Send size={20} />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-700">Talep Bilgileri</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400">Durum</p>
                <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium border mt-1 ${statusColors[ticket.Status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{ticket.Status}</span>
              </div>
              <div>
                <p className="text-xs text-slate-400">Kategori</p>
                <p className="text-sm font-medium text-slate-700 mt-0.5">{ticket.Category}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Öncelik</p>
                <p className="text-sm font-medium text-slate-700 mt-0.5">{ticket.Priority}</p>
              </div>
              {ticket.AssignedToName && (
                <div>
                  <p className="text-xs text-slate-400">İlgilenen Personel</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {ticket.AssignedToName.charAt(0)}
                    </div>
                    <p className="text-sm font-medium text-slate-700">{ticket.AssignedToName}</p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-400">Oluşturulma</p>
                <p className="text-sm text-slate-700 mt-0.5">{new Date(ticket.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
              </div>
              {ticket.UpdatedAt && (
                <div>
                  <p className="text-xs text-slate-400">Son Güncelleme</p>
                  <p className="text-sm text-slate-700 mt-0.5">{new Date(ticket.UpdatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
                </div>
              )}
            </div>
          </div>

          {/* Files */}
          {ticket.files?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Dosyalar</h3>
              <div className="space-y-2">
                {ticket.files.map(file => (
                  <a key={file.Id} href={`${file.FilePath.replace('/uploads', UPLOAD_URL)}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors group">
                    <Download size={16} className="text-slate-400 group-hover:text-blue-600" />
                    <span className="text-sm text-slate-700 truncate group-hover:text-blue-600">{file.OriginalName}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
